import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Download, Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@cargomatic/ui';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';

const columnMapping = {
  SHIPMENT: 'Shipment',
  EQUIPMENT: 'Equipment',
  SHIPPER: 'Shipper',
  CARRIER: 'Carrier',
  ACCESSORIAL_TYPE: 'Accessorial Type',
  SHIPPER_COST: 'Shipper Cost',
  CARRIER_COST: 'Carrier Cost',
  REASON_CODE: 'Reason Code',
  PAYEE: 'Payee',
  ACTION_REQUIRED: 'Action Required',
  APPROVAL_DUE_DATE: 'Approval Due Date',
  ORIGIN_ADDRESS: 'Origin Address',
  DESTINATION_ADDRESS: 'Destination Address',
  DELIVER_AFTER: 'Deliver After',
  DELIVER_BY: 'Deliver By',
  SHIPPER_STATUS: 'Shipper Status',
  CARRIER_STATUS: 'Carrier Status',
  LAST_UPDATED: 'Last Updated',
};

const allColumnKeys = Object.keys(columnMapping);

export const ExportAccessorialExceptionCSV = ({
  exportCSV,
  exportAccessorialsToCSV,
  className,
}) => {
  const { t } = useTranslation();
  const [selectedColumns, setSelectedColumns] = useState([...allColumnKeys]);
  const [open, setOpen] = useState(false);

  const handleColumnToggle = (key) => {
    setSelectedColumns((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      const targetIndex = allColumnKeys.indexOf(key);
      const insertAt = prev.findIndex(
        (k) => allColumnKeys.indexOf(k) > targetIndex,
      );
      if (insertAt === -1) return [...prev, key];
      const next = [...prev];
      next.splice(insertAt, 0, key);
      return next;
    });
  };

  const allSelected = selectedColumns.length === allColumnKeys.length;

  const handleSelectAll = () => {
    setSelectedColumns(allSelected ? [] : [...allColumnKeys]);
  };

  const handleExport = () => {
    exportAccessorialsToCSV(selectedColumns);
    setOpen(false);
  };

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" disabled={!exportCSV} className="gap-2">
            {exportCSV ? (
              <>
                <Download className="size-4" />
                {t('EXPORT_CSV')}
              </>
            ) : (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t('EXPORTING_CSV')}
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="end">
          <div className="max-h-80 overflow-y-auto space-y-1">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAll}
              />
              {t('SELECT_ALL_COLUMNS')}
            </label>
            <Separator className="my-1" />
            {allColumnKeys.map((key) => (
              <label
                key={key}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={selectedColumns.includes(key)}
                  onCheckedChange={() => handleColumnToggle(key)}
                />
                {key === 'SHIPMENT'
                  ? titleize(t(key, { count: 1 }))
                  : titleize(t(sluggify(columnMapping[key])))}
              </label>
            ))}
          </div>
          <Separator className="my-2" />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleExport}
              disabled={!exportCSV || selectedColumns.length === 0}
            >
              {titleize(t('DOWNLOAD'))}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedColumns([])}
            >
              {t('CLEAR_ALL')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

ExportAccessorialExceptionCSV.propTypes = {
  exportCSV: PropTypes.bool.isRequired,
  exportAccessorialsToCSV: PropTypes.func.isRequired,
  className: PropTypes.string,
};
